import { SSE } from 'sse.js';
import Maybe from 'true-myth/maybe';

import type { Pathname, RequestHeaders } from '../http-fetcher/http-fetcher';
import type { HTTPMethod } from '../http-fetcher/http-method';

export type RealtimeEventListener = {
    readonly addEventListener: SSE['addEventListener'];
    readonly start: SSE['stream'];
    readonly stop: SSE['close'];
};

export type RealtimeEventListenerBuilder = {
    readonly build: ({
        autoReconnect,
        method,
        path,
        payload,
        requestHeaders,
    }: {
        readonly autoReconnect?: boolean;
        readonly method?: 'GET' | 'POST';
        readonly path: Pathname;
        readonly payload?: FormData | string;
        readonly requestHeaders?: RequestHeaders;
    }) => RealtimeEventListener;
    set requestHeaders(_requestHeaders: RequestHeaders);
};

const buildListener: (
    baseUrl: URL,
    path: Pathname,
    requestHeaders: Maybe<RequestHeaders>,
    payload?: FormData | string,
    method?: HTTPMethod,
    autoReconnect?: boolean,
) => RealtimeEventListener = (baseUrl, path, requestHeaders, payload, method, autoReconnect = true) => {
    const basePath = baseUrl.pathname.replace(/\/+$/, '');
    const extendedPath = path.replace(/^\/+/, '');

    const actualPath = new URL(`${basePath}/${extendedPath}`, baseUrl);

    const sse = new SSE(`${actualPath}`, {
        autoReconnect,
        debug: true,
        headers: requestHeaders.unwrapOr({}),
        maxRetries: autoReconnect ? 10 : 0,
        method,
        payload,
        reconnectDelay: 1000,
        start: false,
        withCredentials: true,
    });

    return {
        addEventListener: (type, listener) => {
            sse.addEventListener(type, listener);
        },
        start: () => {
            sse.stream();
        },
        stop: () => {
            sse.close();
        },
    };
};

export const createRealtimeEventListenerBuilder: (baseUrl: URL) => RealtimeEventListenerBuilder = (baseUrl) => {
    let defaultRequestHeaders: Maybe<RequestHeaders> = Maybe.nothing();

    return {
        build: ({ autoReconnect = true, method = 'POST', path, payload, requestHeaders = {} }) =>
            buildListener(
                baseUrl,
                path,
                defaultRequestHeaders.map((defaultHeaders) => ({ ...defaultHeaders, ...requestHeaders })),
                payload,
                method,
                autoReconnect,
            ),
        set requestHeaders(_requestHeaders: RequestHeaders) {
            defaultRequestHeaders = defaultRequestHeaders.mapOrElse(
                () => Maybe.of(_requestHeaders),
                (headers) =>
                    Maybe.of<RequestHeaders>({
                        ...headers,
                        ..._requestHeaders,
                    }),
            );
        },
    };
};
